import React, { useState, useEffect, useMemo } from "react";
import { DataTable } from "./ui";
import { MDBContainer, MDBRow, MDBCol, MDBTypography } from "mdb-react-ui-kit";
import Toolbar from "./Toolbar";
import { useTranslation } from "react-i18next";
import OrderStatsCard from "./OrdersReportComponents/OrderStatsCard";
import { api } from "../services";
import { toast } from "react-toastify";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import { format, subDays, subMonths } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import PriceDisplay from "./PriceDisplay";

const SALES_REPORT_DEFAULT_SORT = {
  Branch: { column: "netSales", field: "netSales", direction: "desc" },
  Product: { column: "sales", field: "sales", direction: "desc" },
  Platform: { column: "revenue", field: "revenue", direction: "desc" },
};

const getSalesReportSortApiField = (tab, column, isArabic) => {
  if (tab === "Product" && column === "product") {
    return isArabic ? "productNameAr" : "productNameEn";
  }
  const map = {
    Branch: {
      branch: "branch",
      avgOrderValue: "avgOrderValue",
      netSales: "netSales",
      taxes: "taxes",
      netSaleWithTax: "netSaleWithTax",
      ordersCount: "ordersCount",
      netQuantity: "netQuantity",
      grossSales: "grossSales",
      grossSalesPct: "grossSalesPct",
    },
    Product: {
      product: "productNameEn",
      price: "price",
      soldQuantity: "soldQuantity",
      sales: "sales",
      taxTotal: "taxTotal",
      salesTaxInclusive: "salesTaxInclusive",
      costOfGoodsSold: "costOfGoodsSold",
      grossMargin: "grossMargin",
    },
    Platform: {
      platform: "platform",
      branch: "branch",
      ordersCount: "ordersCount",
      revenue: "revenue",
      commissionPct: "commissionPct",
      netPayment: "netPayment",
      cancelledPct: "cancelledPct",
      timeoutPct: "timeoutPct",
      rejectedPct: "rejectedPct",
      avgSlaMin: "avgSlaMin",
      lastSync: "lastSync",
    },
  };
  return map[tab]?.[column];
};

function SalesReportsPage() {
  const { t, i18n } = useTranslation("translation");
  const { can } = usePermission();
  const canExportReport = can(RBAC_MODULES.SALES_REPORTS, PERMISSION_ACTIONS.EXPORT);
  const noPermissionTip = t("noPermissionToAccessThis");
  const isArabic = i18n.language === "ar";
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({
    totalOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    rejectedOrders: 0,
    timeoutOrders: 0,
    completedPercentage: "0",
    cancelledPercentage: "0",
    rejectedPercentage: "0",
    timeoutPercentage: "0",
  });

  const [dateRange, setDateRange] = useState({ dateFrom: "", dateTo: "" });
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [storesOptions, setStoresOptions] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [storesPayload, setStoresPayload] = useState(null);
  const [storesList, setStoresList] = useState([]);
  const [branchesOptions, setBranchesOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState("");
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasPrevPage, setHasPrevPage] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTabSales, setActiveTabSales] = useState("Branch");
  const [tableSortColumn, setTableSortColumn] = useState(
    SALES_REPORT_DEFAULT_SORT.Branch.column
  );
  const [reportSortField, setReportSortField] = useState(
    SALES_REPORT_DEFAULT_SORT.Branch.field
  );
  const [reportSortDirection, setReportSortDirection] = useState(
    SALES_REPORT_DEFAULT_SORT.Branch.direction
  );
  const tabs = ["Branch", "Product", "Platform"];

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const h = setTimeout(() => {
      setDebouncedCategorySearch((categorySearch || "").trim());
    }, 700);
    return () => clearTimeout(h);
  }, [categorySearch]);

  useEffect(() => {
    fetchOrders(page);
  }, [
    debouncedSearch,
    dateRange,
    selectedPlatform,
    selectedBranchId,
    selectedCategoryId,
    activeTab,
    selectedStoreId,
    page,
    limit,
    activeTabSales,
    reportSortField,
    reportSortDirection,
  ]);

  const fetchOrders = async (
    pageArg = 1,
    sortBy = reportSortField,
    sortOrder = reportSortDirection
  ) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();

      params.append("page", String(pageArg));
      params.append("limit", String(limit));
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);

      if (selectedCategoryId) params.append("categoryId", selectedCategoryId);
      if (activeTab && activeTab !== "ALL") params.append("status", activeTab);
      if (selectedBranchId) params.append("branchId", selectedBranchId);
      if (selectedStoreId) params.append("storeId", selectedStoreId);
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (selectedPlatform) params.append("platform", selectedPlatform);
      if (dateRange.dateFrom) params.append("startDate", dateRange.dateFrom);
      if (dateRange.dateTo) params.append("endDate", dateRange.dateTo);

  

      let endpoint = "/sales-reports";
      if (activeTabSales === "Branch") endpoint = "/sales-reports/branch";
      else if (activeTabSales === "Product")
        endpoint = "/sales-reports/product";
      else if (activeTabSales === "Platform")
        endpoint = "/sales-reports/platform";

      const response = await api.get(`${endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const items = response.data?.data?.rows || response.data?.orders || [];
      setOrders(items);
      setSelectedIds([]);
      if (response.data?.data?.summary) setSummary(response.data.data.summary);
      const pag =
        response.data?.pagination || response.data?.data?.pagination || null;
      if (pag) {
        setPage(Number(pag.currentPage) || pageArg);
        setLimit(Number(pag.itemsPerPage) || limit);
        setTotal(Number(pag.totalItems) || items.length);
        setTotalPages(
          Number(pag.totalPages) ||
            Math.max(
              1,
              Math.ceil(
                (Number(pag.totalItems) || items.length) /
                  (Number(pag.itemsPerPage) || limit)
              )
            )
        );
        setHasPrevPage(Boolean(pag.hasPrevPage));
        setHasNextPage(Boolean(pag.hasNextPage));
      } else {
        const totalItems =
          response.data?.totalItems ??
          response.data?.total ??
          response.data?.data?.total ??
          items.length;
        const totalPagesResp =
          response.data?.totalPages ??
          response.data?.data?.totalPages ??
          Math.max(1, Math.ceil((totalItems || items.length) / limit));
        setTotal(totalItems || items.length);
        setTotalPages(totalPagesResp);
        setPage(pageArg);
        setHasPrevPage(null);
        setHasNextPage(null);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setLoading(false);
      // Fallback for demo purposes if API fails or doesn't exist yet
      setOrders([]);
      setSelectedIds([]);
      setSummary({
        totalOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        rejectedOrders: 0,
        timeoutOrders: 0,
        completedPercentage: "0",
        cancelledPercentage: "0",
        rejectedPercentage: "0",
        timeoutPercentage: "0",
      });
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStoresPayload(response.data);
      const stores = response.data?.stores || [];
      setStoresList(stores);
      const options = stores.map((s) => ({
        _id: s?._id,
        name: {
          en: s?.name || s?.name_en || s?.displayName || "",
          ar: s?.name_ar || s?.displayNameAr || s?.name || "",
        },
      }));
      setStoresOptions(options);
    } catch (err) {
      setStoresOptions([]);
    }
  };

  const updateBranchesForStore = (storeId) => {
    const store = storesList.find((s) => s?._id === storeId);
    const branches = store?.branches || [];
    const options = branches.map((b) => ({
      _id: b?._id,
      name: {
        en: b?.name?.en || b?.name || b?.displayName || "",
        ar: b?.name?.ar || b?.name_ar || b?.displayNameAr || b?.name || "",
      },
    }));
    setBranchesOptions(options);
  };

  const fetchCategoriesForStore = async (storeId, searchTerm = "") => {
    if (!storeId) {
      setCategoriesOptions([]);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("limit", "100");
      params.set("storeId", storeId);
      if ((searchTerm || "").trim())
        params.set("search", (searchTerm || "").trim());

      const response = await api.get(`/categories?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const cats = Array.isArray(response.data?.categories)
        ? response.data.categories
        : Array.isArray(response.data?.data?.categories)
        ? response.data.data.categories
        : [];

      const options = cats.map((c) => ({
        _id: c?._id,
        name: {
          en: c?.name?.en || c?.name || c?.displayName || "",
          ar: c?.name?.ar || c?.name_ar || c?.displayNameAr || c?.name || "",
        },
      }));
      setCategoriesOptions(options);
    } catch (err) {
      console.error(
        "Error fetching categories:",
        err.response?.data || err.message
      );
      setCategoriesOptions([]);
    }
  };

  useEffect(() => {
    if (!selectedStoreId) return;
    fetchCategoriesForStore(selectedStoreId, debouncedCategorySearch);
  }, [selectedStoreId, debouncedCategorySearch]);

  const [platformOptions, setPlatformOptions] = useState([]);

  const fetchPlatforms = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/platforms", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (
        response.data &&
        response.data.success &&
        response.data.data &&
        response.data.data.platforms
      ) {
        const platforms = response.data.data.platforms;
        const options = platforms.map((p) => ({
          _id: p.key,
          name: {
            en: p.nameEn || p.name,
            ar: p.nameAr || p.name,
          },
        }));
        setPlatformOptions(options);
      } else {
        setPlatformOptions([]);
      }
    } catch (err) {
      console.error("Error fetching platforms:", err);
      setPlatformOptions([]);
    }
  };

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const formatDateTime = (value) => {
    const d = new Date(value);
    if (isNaN(d.getTime())) return { date: "-", time: "-" };
    return { date: format(d, "M/d/yyyy"), time: format(d, "h:mm:ss a") };
  };

  const SarAmount = ({ value, decimals = 2 }) => (
    <PriceDisplay value={value} decimals={decimals} iconSize={11} color="#030F1B" />
  );

  const applyTableSort = (column, direction) => {
    const apiField = getSalesReportSortApiField(
      activeTabSales,
      column,
      isArabic
    );
    if (!apiField) return;

    setTableSortColumn(column);
    setReportSortField(apiField);
    setReportSortDirection(direction);
    setPage(1);
  };

  const salesColumns = useMemo(() => {
    const th = (key) => t(`Reports.TableHeaders.${key}`);
    if (activeTabSales === "Branch") {
      return [
        {
          id: "branch",
          header: th("Branch"),
          strong: true,
          sortable: true,
          render: (item) =>
            isArabic
              ? item.branchNameAr || item.branchName || "-"
              : item.branchName || "-",
        },
        {
          id: "avgOrderValue",
          header: th("AvgOrderValue"),
          sortable: true,
          render: (item) => <SarAmount value={item.avgOrderValue} />,
        },
        {
          id: "netSales",
          header: th("NetSales"),
          sortable: true,
          render: (item) => <SarAmount value={item.netSales} />,
        },
        {
          id: "taxes",
          header: th("Taxes"),
          sortable: true,
          render: (item) => <SarAmount value={item.taxes} />,
        },
        {
          id: "netSaleWithTax",
          header: th("NetSaleWithTax"),
          sortable: true,
          render: (item) => <SarAmount value={item.netSaleWithTax} />,
        },
        {
          id: "ordersCount",
          header: th("OrdersCount"),
          sortable: true,
          render: (item) => item.ordersCount || 0,
        },
        {
          id: "netQuantity",
          header: th("NetQuantity"),
          sortable: true,
          render: (item) => item.netQuantity || 0,
        },
        {
          id: "grossSales",
          header: th("GrossSales"),
          sortable: true,
          render: (item) => <SarAmount value={item.grossSales} decimals={null} />,
        },
        {
          id: "grossSalesPct",
          header: th("GrossSalesPercentage"),
          sortable: true,
          render: (item) => `${item.grossSalesPct || 0}%`,
        },
        {
          id: "cost",
          header: th("Cost"),
          render: (item) => <SarAmount value={item.cost} decimals={null} />,
        },
      ];
    }
    if (activeTabSales === "Product") {
      return [
        {
          id: "product",
          header: th("Product"),
          strong: true,
          sortable: true,
          render: (item) =>
            isArabic
              ? item.productNameAr || item.productNameEn || item.productName || "-"
              : item.productNameEn || item.productName || "-",
        },
        {
          id: "price",
          header: th("Price"),
          sortable: true,
          render: (item) => <SarAmount value={item.price ?? "0"} />,
        },
        {
          id: "soldQuantity",
          header: th("SoldQuantity"),
          sortable: true,
          render: (item) => item.soldQuantity || 0,
        },
        {
          id: "sales",
          header: th("Sales"),
          sortable: true,
          render: (item) => <SarAmount value={item.sales} />,
        },
        {
          id: "taxTotal",
          header: th("Tax"),
          sortable: true,
          render: (item) => <SarAmount value={item.taxTotal} />,
        },
        {
          id: "salesTaxInclusive",
          header: th("SalesTaxInclusive"),
          sortable: true,
          render: (item) => <SarAmount value={item.salesTaxInclusive} />,
        },
        {
          id: "costOfGoodsSold",
          header: th("CostOfGoodsSold"),
          sortable: true,
          render: (item) => <SarAmount value={item.costOfGoodsSold} decimals={null} />,
        },
        {
          id: "grossMargin",
          header: th("GrossMargin"),
          sortable: true,
          render: (item) => <SarAmount value={item.grossMargin} decimals={null} />,
        },
      ];
    }
    return [
      {
        id: "platform",
        header: th("platform"),
        strong: true,
        sortable: true,
        render: (item) => item.platform || "-",
      },
      {
        id: "branch",
        header: th("Branch"),
        sortable: true,
        render: (item) =>
          isArabic && item.branchNameAr ? item.branchNameAr : item.branchName,
      },
      {
        id: "ordersCount",
        header: th("OrdersCount"),
        sortable: true,
        render: (item) => item.ordersCount || 0,
      },
      {
        id: "revenue",
        header: th("Revenue"),
        sortable: true,
        render: (item) => <SarAmount value={item.revenue} />,
      },
      {
        id: "commissionPct",
        header: th("CommissionPct"),
        sortable: true,
        render: (item) => `${item.commissionPct ? Number(item.commissionPct).toFixed(2) : 0}%`,
      },
      {
        id: "netPayment",
        header: th("NetPayment"),
        sortable: true,
        render: (item) => <SarAmount value={item.netPayment} />,
      },
      {
        id: "cancelledPct",
        header: th("CancelledOrdersPercentage"),
        sortable: true,
        render: (item) => `${item.cancelledPct || 0}%`,
      },
      {
        id: "timeoutPct",
        header: th("TimeOutOrdersPercentage"),
        sortable: true,
        render: (item) => `${item.timeoutPct || 0}%`,
      },
      {
        id: "rejectedPct",
        header: th("RejectedOrdersPercentage"),
        sortable: true,
        render: (item) => `${item.rejectedPct || 0}%`,
      },
      {
        id: "avgSlaMin",
        header: th("AverageSlaMin"),
        sortable: true,
        render: (item) => item.avgSlaMin || 0,
      },
      {
        id: "lastSync",
        header: th("LastSync"),
        sortable: true,
        render: (item) => {
          const dt = formatDateTime(item.lastSync);
          return (
            <div className="d-flex flex-column">
              <span>{dt.date}</span>
              <span>{dt.time}</span>
            </div>
          );
        },
      },
    ];
  }, [activeTabSales, isArabic, t]);

  const handleExport = async () => {
    if (!canExportReport) {
      toast.error(noPermissionTip);
      return;
    }
    try {
      setIsExporting(true);
      const params = new URLSearchParams();
      params.append("type", activeTabSales.toLowerCase());
      if (selectedCategoryId) params.append("categoryId", selectedCategoryId);
      if (activeTab && activeTab !== "ALL") params.append("status", activeTab);
      if (selectedBranchId) params.append("branchId", selectedBranchId);
      if (selectedStoreId) params.append("storeId", selectedStoreId);
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (selectedPlatform) params.append("platform", selectedPlatform);
      if (dateRange.dateFrom) params.append("startDate", dateRange.dateFrom);
      if (dateRange.dateTo) params.append("endDate", dateRange.dateTo);

      const res = await api.get(`/sales-reports/export?${params.toString()}`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      let filename = "sales-reports.csv";
      const cd = res.headers && res.headers["content-disposition"];
      if (cd) {
        const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
        if (match && match[1]) filename = match[1].replace(/['"]/g, "");
      }
      const typeSlug = (activeTabSales || "").toLowerCase();
      const dotIdx = filename.lastIndexOf(".");
      const base = dotIdx > -1 ? filename.slice(0, dotIdx) : filename;
      const ext = dotIdx > -1 ? filename.slice(dotIdx) : ".csv";
      filename = `${base}-${typeSlug}${ext}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting reports:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const exportPrimaryAction = canExportReport
    ? {
        label: isExporting
          ? isArabic
            ? "جاري التصدير"
            : "Exporting"
          : isArabic
          ? "تصدير"
          : "Export",
        onClick: handleExport,
        disabled: isExporting,
        icon: (
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            style={{ transform: isArabic ? "scaleX(-1)" : "none" }}
          />
        ),
      }
    : undefined;

  const reportFilterDropdowns = useMemo(
    () => [
      {
        label: t("Reports.brand"),
        placeholder: t("Reports.brand"),
        options: storesOptions,
        selectedOption: selectedStoreId || null,
        handleSelect: (opt) => {
          const id = opt?._id || "";
          setSelectedStoreId(id);
          updateBranchesForStore(id);
          setSelectedBranchId("");
          fetchCategoriesForStore(id, "");
          setSelectedCategoryId("");
          setPage(1);
        },
      },
      {
        label: t("Reports.branch"),
        placeholder: t("Reports.branch"),
        options: branchesOptions,
        selectedOption: selectedBranchId || null,
        disabled: false,
        emptyMessage: !selectedStoreId
          ? t("Reports.selectBrandFirst")
          : t("Reports.noOptionsFound"),
        handleSelect: (opt) => {
          const id = opt?._id || "";
          setSelectedBranchId(id);
          setPage(1);
        },
      },
      {
        label: t("Reports.category"),
        placeholder: t("Reports.category"),
        options: categoriesOptions,
        selectedOption: selectedCategoryId || null,
        disabled: false,
        emptyMessage:
          !selectedStoreId || !selectedBranchId
            ? t("Reports.selectBrandFirst")
            : t("Reports.noOptionsFound"),
        onSearchChange: (term) => setCategorySearch(term),
        handleSelect: (opt) => {
          const id = opt?._id || "";
          setSelectedCategoryId(id);
          setPage(1);
        },
      },
      {
        label: t("Reports.platform"),
        placeholder: t("Reports.platform"),
        options: platformOptions,
        selectedOption: selectedPlatform || null,
        handleSelect: (opt) => {
          const id = (opt?._id || "").toUpperCase();
          setSelectedPlatform(id);
          setPage(1);
        },
      },
    ],
    [
      t,
      storesOptions,
      selectedStoreId,
      branchesOptions,
      selectedBranchId,
      categoriesOptions,
      selectedCategoryId,
      platformOptions,
      selectedPlatform,
    ]
  );

  return (
    <div className="reports-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">
          {t("Reports.salesReports")}
        </h1>

        <div className="oh-page-section">
        <div className="oh-reports-tabs">
          <MDBRow className="mx-0 align-items-center">
            {tabs.map((tab) => {
              const isActive = activeTabSales === tab;

              return (
                <MDBCol
                  size="auto"
                  key={tab}
                  onClick={() => {
                    const defaults = SALES_REPORT_DEFAULT_SORT[tab];
                    setActiveTabSales(tab);
                    setSelectedIds([]);
                    setTableSortColumn(defaults.column);
                    setReportSortField(defaults.field);
                    setReportSortDirection(defaults.direction);
                    setPage(1);
                  }}
                  className="px-3"
                  style={{ cursor: "pointer" }}
                >
                  <div
                    className={`pb-1 ${isActive ? "fw-bold" : "text-muted"}`}
                    style={{
                      fontSize: "0.9rem",
                      borderBottom: isActive
                        ? "3px solid #030F1B"
                        : "3px solid transparent",
                      transition: "all 0.2s ease-in-out",
                      color: isActive ? "#030F1B" : undefined,
                    }}
                  >
                    {t(`Reports.Tabs.${tab}`)}
                  </div>
                </MDBCol>
              );
            })}
          </MDBRow>
        </div>
        </div>

        <div className="oh-page-section">
          {activeTabSales === "Branch" && (
            <div className="oh-kpi-row oh-kpi-row--4">
                <OrderStatsCard
                  title={t("Reports.Cards.TopPerformingBranch")}
                  value={isArabic
  ? summary.topPerformingBranch?.branchNameAr || summary.topPerformingBranch?.branchName
  : summary.topPerformingBranch?.branchName}
                  loading={loading}
                  textValue
                />
                <OrderStatsCard
                  title={t("Reports.Cards.TotalOrders")}
                  value={
                    summary.totalOrders
                      ? summary.totalOrders.toLocaleString()
                      : 0
                  }
                  loading={loading}
                />
                <OrderStatsCard
                  title={t("Reports.Cards.TotalBranchSales")}
                  value={
                    <PriceDisplay
                      value={
                        summary.totalRevenue
                          ? summary.totalRevenue.toLocaleString()
                          : "0"
                      }
                      iconSize={16}
                      decimals={null}
                      color="#030f1b"
                    />
                  }
                  loading={loading}
                />
                <OrderStatsCard
                  title={t("Reports.Cards.AvgOrderValue")}
                  value={
                    <PriceDisplay
                      value={
                        summary.avgOrderValue
                          ? Number(summary.avgOrderValue).toFixed(2)
                          : "0"
                      }
                      iconSize={16}
                      decimals={null}
                      color="#030f1b"
                    />
                  }
                  loading={loading}
                />
            </div>
          )}

          {activeTabSales === "Product" && (
            <div className="oh-kpi-row oh-kpi-row--3">
                <OrderStatsCard
                  title={t("Reports.Cards.TopProduct")}
                  value={isArabic? summary.topProduct?.name_ar || summary.topProduct?.name_en || "-" : summary.topProduct?.name_en || "-"}
                  loading={loading}
                  textValue
                />
                <OrderStatsCard
                  title={t("Reports.Cards.TotalSoldQuantity")}
                  value={
                    summary.totalSoldQuantity
                      ? summary.totalSoldQuantity.toLocaleString()
                      : 0
                  }
                  loading={loading}
                />
                <OrderStatsCard
                  title={t("Reports.Cards.TotalGrossMargin")}
                  value={
                    <PriceDisplay
                      value={
                        summary.totalGrossMargin
                          ? summary.totalGrossMargin.toLocaleString()
                          : "0"
                      }
                      iconSize={16}
                      decimals={null}
                      color="#030f1b"
                    />
                  }
                  loading={loading}
                />
            </div>
          )}
          {activeTabSales === "Platform" && (
            <div className="oh-kpi-row oh-kpi-row--2">
                <OrderStatsCard
                  title={t("Reports.Cards.TopPlatformBySales")}
                  value={isArabic ? summary.topPlatformBySales?.platformNameAr : summary.topPlatformBySales?.platform}
                  loading={loading}
                  textValue
                />
                <OrderStatsCard
                  title={t("Reports.Cards.FastestPlatformAvgSLA")}
                  value={isArabic ? summary.fastestPlatformAvgSla?.platformNameAr : summary.fastestPlatformAvgSla?.platform }
                  loading={loading}
                  textValue
                />
            </div>
          )}
        </div>

        <div className="oh-page-section">
          <Toolbar
            toolbarVariant="reports"
            hideIcons={true}
            showDateRange={true}
            dateFrom={dateRange.dateFrom}
            onDateFromChange={(e) => {
              setDateRange((prev) => ({ ...prev, dateFrom: e.target.value }));
              setPage(1);
            }}
            dateTo={dateRange.dateTo}
            onDateToChange={(e) => {
              setDateRange((prev) => ({ ...prev, dateTo: e.target.value }));
              setPage(1);
            }}
            extraDropdowns={reportFilterDropdowns}
          />
        </div>

        <div className="oh-page-section">
      <DataTable
        tableId={`sales-reports-${activeTabSales.toLowerCase()}`}
        title={t(`Reports.Tabs.${activeTabSales}`)}
        columns={salesColumns}
        data={orders}
        rowKey={(row, i) => row._id || `${activeTabSales}-${i}`}
        loading={loading}
        emptyState={t("No Data Available")}
        isRtl={isArabic}
        selectable
        selectionMode="checkbox"
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        bulkBarPlacement="end"
        selectedLabel={t("Selected", { defaultValue: "Selected" })}
        selectAllLabel={t("Select All", { defaultValue: "Select All" })}
        primaryAction={exportPrimaryAction}
        sort={{
          column: tableSortColumn,
          direction: reportSortDirection,
          onChange: ({ column, direction }) =>
            applyTableSort(column, direction),
        }}
        pagination={{
          page,
          pageSize: limit,
          total,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setLimit(size);
            setPage(1);
          },
          showLabel: t("show"),
          ofLabel: t("of"),
          resultsLabel: t("results", { defaultValue: "results" }),
          isRtl: isArabic,
          previousLabel: t("previous"),
          nextLabel: t("next"),
        }}
      />
        </div>
      </MDBContainer>
    </div>
  );
}

export default SalesReportsPage;
